import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PoolMessageEditor } from '@/components/PoolMessageEditor'
import type { CampaignConfig } from '@sdr-agend/admin-schemas'

/**
 * Story 5.8 — Reactivation campaign config section.
 *
 * Rendered by `CampaignDetail.tsx` ONLY when `campaign.type === 'reactivation'`.
 * Wires the manager-editable knobs that Story 5.6 (reactivation scheduler)
 * and Story 5.7 (cold lead handler) consume:
 *
 *   - Pool de mensagens (`reactivation_pool: string[]`) — Story 5.6 rotates
 *   - Thresholds opcionais per-campaign (`reactivation_thresholds: string[]`)
 *   - Manager phone (`manager_phone`) — Story 5.7 cold alert recipient
 *   - Notify toggle (`reactivation_notify_manager`) — gates the cold alert
 */
export interface ReactivationConfigSectionProps {
  value: CampaignConfig
  onChange: (next: CampaignConfig) => void
  disabled?: boolean
}

const DEFAULT_THRESHOLDS: ReadonlyArray<string> = ['1 day', '3 days', '7 days']

export function ReactivationConfigSection(
  props: ReactivationConfigSectionProps,
): React.ReactElement {
  const { value, onChange, disabled = false } = props

  function patch<K extends keyof CampaignConfig>(key: K, val: CampaignConfig[K]): void {
    onChange({ ...value, [key]: val })
  }

  const thresholds = value.reactivation_thresholds ?? DEFAULT_THRESHOLDS

  function updateThreshold(idx: number, text: string): void {
    const next = thresholds.slice()
    next[idx] = text
    // Only persist when the value differs from the global default — keeps
    // the JSONB clean (saves bytes + matches "absent means inherit").
    const cleaned = next.filter((t) => t.trim().length > 0)
    const equalsDefault =
      cleaned.length === DEFAULT_THRESHOLDS.length &&
      cleaned.every((t, i) => t === DEFAULT_THRESHOLDS[i])
    patch('reactivation_thresholds', equalsDefault ? undefined : cleaned)
  }

  return (
    <section className="grid gap-6 rounded-lg border bg-muted/20 p-5">
      <header>
        <h2 className="text-lg font-semibold">Configuração Reativação</h2>
        <p className="text-sm text-muted-foreground">
          Campos do fluxo de reativação de leads silenciosos.
        </p>
      </header>

      {/* Thresholds. */}
      <div className="grid gap-2">
        <Label>Thresholds de silêncio</Label>
        <div className="grid gap-2 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Input
              key={i}
              value={thresholds[i] ?? ''}
              onChange={(e) => updateThreshold(i, e.target.value)}
              disabled={disabled}
              placeholder={DEFAULT_THRESHOLDS[i]}
              aria-label={`Threshold tentativa ${i + 1}`}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Postgres interval strings (ex: <code>1 day</code>, <code>3 days</code>, <code>7 days</code>).
          Vazio em todos = usa o global <code>system_settings.reactivation_thresholds</code>.
        </p>
      </div>

      {/* Pool editor. */}
      <PoolMessageEditor
        label="Pool de mensagens (free-form)"
        value={value.reactivation_pool ?? []}
        onChange={(next) =>
          patch('reactivation_pool', next.length > 0 ? next : undefined)
        }
        minEntries={5}
        maxChars={1000}
        disabled={disabled}
      />

      {/* Manager phone (Story 5.7). */}
      <div className="grid gap-2 sm:max-w-sm">
        <Label htmlFor="rt-mgr-phone">Telefone do gerente (E.164)</Label>
        <Input
          id="rt-mgr-phone"
          value={value.manager_phone ?? ''}
          onChange={(e) =>
            patch('manager_phone', e.target.value.length > 0 ? e.target.value : undefined)
          }
          disabled={disabled}
          placeholder="+5511999999999"
        />
        <p className="text-xs text-muted-foreground">
          Recebe o alerta quando um lead esfria (Story 5.7). Vazio = usa o primeiro de{' '}
          <code>system_settings.handoff_operators</code>.
        </p>
      </div>

      {/* Notify toggle. */}
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id="rt-notify"
          className="mt-1 h-4 w-4 rounded border-input"
          checked={value.reactivation_notify_manager ?? true}
          onChange={(e) => patch('reactivation_notify_manager', e.target.checked)}
          disabled={disabled}
        />
        <div className="grid gap-1">
          <Label htmlFor="rt-notify" className="cursor-pointer">
            Notificar gerente após 3ª tentativa sem resposta
          </Label>
          <p className="text-xs text-muted-foreground">
            Quando desligado, leads esfriam silenciosamente — o card de Cold Rate ainda atualiza.
          </p>
        </div>
      </div>
    </section>
  )
}

export default ReactivationConfigSection
